import PropTypes from 'prop-types';
import { createContext, useCallback, useContext, useRef, useState } from 'react';
// @mui
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Stack, Typography } from '@mui/material';
// components
import Iconify from './Iconify';

// ----------------------------------------------------------------------
// One "are you sure?" dialog for the whole app.
//   const confirm = useConfirm();
//   if (!(await confirm({ title: 'Delete attribute?', name: row.name }))) return;
// Resolves true on Delete, false on Cancel / Esc / click outside.

const ConfirmContext = createContext(null);

const DEFAULTS = {
  title: 'Delete this item?',
  name: '',
  message: "This can't be undone.",
  confirmText: 'Delete',
  cancelText: 'Cancel',
  color: 'error',
};

ConfirmProvider.propTypes = {
  children: PropTypes.node,
};

export function ConfirmProvider({ children }) {
  const [options, setOptions] = useState(null);
  const resolver = useRef(null);

  const confirm = useCallback(
    (opts = {}) =>
      new Promise((resolve) => {
        resolver.current = resolve;
        setOptions({ ...DEFAULTS, ...opts });
      }),
    []
  );

  const close = (answer) => {
    resolver.current?.(answer);
    resolver.current = null;
    setOptions(null);
  };

  const o = options || DEFAULTS;

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      <Dialog open={!!options} onClose={() => close(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ pb: 1 }}>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Box
              sx={{
                width: 40,
                height: 40,
                flexShrink: 0,
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: `${o.color}.main`,
                bgcolor: (theme) => theme.palette[o.color]?.lighter || theme.palette.action.hover,
              }}
            >
              <Iconify icon={o.color === 'error' ? 'eva:trash-2-outline' : 'eva:alert-triangle-outline'} width={22} height={22} />
            </Box>
            <span>{o.title}</span>
          </Stack>
        </DialogTitle>
        <DialogContent sx={{ pt: '8px !important' }}>
          {o.name && (
            <Typography variant="subtitle1" sx={{ mb: 0.5, wordBreak: 'break-word' }}>
              {o.name}
            </Typography>
          )}
          {o.message && (
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              {o.message}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" color="inherit" onClick={() => close(false)}>
            {o.cancelText}
          </Button>
          <Button variant="contained" color={o.color} onClick={() => close(true)} autoFocus>
            {o.confirmText}
          </Button>
        </DialogActions>
      </Dialog>
    </ConfirmContext.Provider>
  );
}

export function useConfirm() {
  const confirm = useContext(ConfirmContext);
  // Outside the provider (shouldn't happen) fall back to the browser prompt rather than deleting silently.
  return confirm || ((opts = {}) => Promise.resolve(window.confirm(opts.title || DEFAULTS.title)));
}
